import { Err, Ok, Result } from 'ts-results-es';

import { RequestInterceptorConfig, ResponseInterceptorConfig } from '../sdks/interceptors.js';
import { getExceptionMessage } from '../utils/getExceptionMessage.js';
import { log } from './logger.js';
import { shouldNotifyWhenLevelIsAtLeast } from './notification.js';

type MaskedKeys = 'data' | 'headers';
type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;
type MaskedRequest = Optional<RequestInterceptorConfig, MaskedKeys>;
type MaskedResponse = Optional<ResponseInterceptorConfig, MaskedKeys>;

export const maskRequest = (config: RequestInterceptorConfig): MaskedRequest => {
  const result = clone<MaskedRequest>(config);
  if (result.isErr()) {
    return config;
  }

  const maskedData = result.value;
  if (shouldNotifyWhenLevelIsAtLeast('debug')) {
    if (maskedData.data?.credentials) {
      maskedData.data.credentials = '<redacted>';
    }

    if (Array.isArray(maskedData?.data?.datasource?.connections)) {
      const connections = maskedData.data.datasource.connections as Array<{
        connectionLuid: string;
        connectionUsername: string;
        connectionPassword: string;
      }>;

      connections.forEach((connection) => {
        connection.connectionUsername = '<redacted>';
        connection.connectionPassword = '<redacted>';
      });
    }

    if (maskedData.headers?.['X-Tableau-Auth']) {
      maskedData.headers['X-Tableau-Auth'] = '<redacted>';
    }

    if (maskedData.headers?.['Authorization']) {
      maskedData.headers['Authorization'] = '<redacted>';
    }

    if (maskedData.params?.['user_id']) {
      maskedData.params['user_id'] = '<redacted>';
    }
  } else {
    delete maskedData.data;
    delete maskedData.headers;
    delete maskedData.params;
  }

  return maskedData;
};

export const maskResponse = (response: ResponseInterceptorConfig): MaskedResponse => {
  if (!shouldNotifyWhenLevelIsAtLeast('debug')) {
    const { data: _data, headers: _headers, ...maskedResponse } = response;
    return maskedResponse;
  }

  if (isBinaryLike(response.data)) {
    return {
      ...response,
      data: {
        redacted: true,
        reason: 'binary-payload',
        byteLength: response.data.byteLength,
      },
    };
  }

  const result = clone<MaskedResponse>(response);
  if (result.isErr()) {
    return response;
  }

  const maskedData = result.value;
  if (maskedData.data?.credentials) {
    maskedData.data.credentials = '<redacted>';
  }

  return maskedData;
};

function isBinaryLike(value: unknown): value is ArrayBuffer | ArrayBufferView {
  return value instanceof ArrayBuffer || ArrayBuffer.isView(value);
}

function clone<T>(obj: T): Result<T, Error> {
  try {
    return Ok(structuredClone(obj));
  } catch (error) {
    if (error instanceof Error) {
      return Err(error);
    }

    log({
      message: 'Could not clone object, notification may not be sanitized!',
      level: 'error',
      logger: 'secretMask',
      data: error,
    });
    return Err(new Error(getExceptionMessage(error)));
  }
}
